import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { POPULAR_BRANDS } from '../config/siteConfig';
import { brandSectionService } from '../services/BrandSectionService';
import { BrandSection } from '../types';
import ProgressiveImage from './ProgressiveImage';
import { useResponsiveDevice } from '../hooks/useResponsiveDevice';

interface AppleStyleGridProps {
    onSelectBrand: (brandName: string) => void;
}

const AppleStyleGrid: React.FC<AppleStyleGridProps> = ({ onSelectBrand }) => {
    const [sections, setSections] = useState<BrandSection[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const currentDeviceType = useResponsiveDevice();

    useEffect(() => {
        const loadSections = async () => {
            try {
                const data = await brandSectionService.getAll();
                // Filter active sections and ensure they are sorted by display_order (handled by service)
                setSections(data.filter(s => s.is_active));
            } catch (error) {
                console.error("Failed to load brand sections", error);
            } finally {
                setIsLoading(false);
            }
        };
        loadSections();
    }, []);

    if (isLoading) return null;

    return (
        <div id="catalog-section" className="pt-6 md:pt-8 pb-10 bg-black relative overflow-hidden">
            {/* Ambient Background Glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-white/5 blur-[120px] rounded-full pointer-events-none" />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="mb-16 text-center">
                    <motion.h2
                        className="text-5xl md:text-7xl font-display font-bold text-white tracking-tighter mb-6 cursor-default group"
                        initial="initial"
                        whileHover="hover"
                    >
                        Elige tu <motion.span
                            variants={{
                                initial: {
                                    color: "rgba(156, 163, 175, 1)",
                                    textShadow: "0px 0px 0px rgba(255, 255, 255, 0)"
                                },
                                hover: {
                                    color: "rgba(255, 255, 255, 1)",
                                    textShadow: "0px 0px 25px rgba(255, 255, 255, 0.6)"
                                }
                            }}
                            transition={{ duration: 1.2, ease: "easeInOut" }}
                        >Ecosistema.</motion.span>
                    </motion.h2>
                    <p className="text-xl text-gray-400 max-w-2xl mx-auto font-light">
                        Protección de fibra de carbono real diseñada con precisión milimétrica para tus dispositivos favoritos.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 auto-rows-auto">
                    {sections.map((section, index) => {
                        // Pattern: (2cols, 1col), (3cols-slim), (1col, 2cols)?
                        // Let's go with a simpler alternating pattern:
                        // Row 1: 2, 1
                        // Row 2: 3 (Slim)
                        // Row 3: 1, 2
                        // Row 4: 3 (Slim)

                        const patternIndex = index % 6;
                        let layoutClass = "h-[400px]";
                        let isFullWidth = false;

                        if (patternIndex === 0) layoutClass = "md:col-span-2 h-[400px]";
                        else if (patternIndex === 1) layoutClass = "md:col-span-1 h-[400px]";
                        else if (patternIndex === 2) { layoutClass = "md:col-span-3 h-[250px]"; isFullWidth = true; }
                        else if (patternIndex === 3) layoutClass = "md:col-span-1 h-[400px]";
                        else if (patternIndex === 4) layoutClass = "md:col-span-2 h-[400px]";
                        else if (patternIndex === 5) { layoutClass = "md:col-span-3 h-[250px]"; isFullWidth = true; }

                        return (
                            <motion.div
                                key={section.id}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: (index % 3) * 0.1 }}
                                onClick={() => onSelectBrand(section.title)}
                                className={`group relative bg-zinc-900/50 backdrop-blur-xl border border-white/10 rounded-3xl overflow-hidden cursor-pointer hover:border-white/20 transition-all duration-500 ${layoutClass}`}
                            >
                                <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                                <div className="absolute inset-0 overflow-hidden rounded-3xl">
                                    {/* Wrapper 1: Handles the base scale and origin from the database */}
                                    <div
                                        className="w-full h-full absolute inset-0 pointer-events-none select-none transition-transform duration-500"
                                        style={{
                                            transformOrigin: section.framing_settings?.[currentDeviceType]
                                                ? `${section.framing_settings[currentDeviceType].x}% ${section.framing_settings[currentDeviceType].y}%`
                                                : (section.position || 'center')
                                        }}
                                    >
                                        {/* Wrapper 2: Handles the hover zoom effect independently */}
                                        <div className="w-full h-full group-hover:scale-105 transition-transform duration-700">
                                            <ProgressiveImage
                                                src={section.image_url}
                                                alt={section.title}
                                                className="group-hover:opacity-80 transition-opacity duration-700 pointer-events-none select-none"
                                                containerClassName="w-full h-full opacity-60 pointer-events-none select-none"
                                                style={{
                                                    objectPosition: section.framing_settings?.[currentDeviceType]
                                                        ? `${section.framing_settings[currentDeviceType].x}% ${section.framing_settings[currentDeviceType].y}%`
                                                        : (section.position || 'center'),
                                                    transformOrigin: section.framing_settings?.[currentDeviceType]
                                                        ? `${section.framing_settings[currentDeviceType].x}% ${section.framing_settings[currentDeviceType].y}%`
                                                        : (section.position || 'center'),
                                                    transform: `scale(${section.framing_settings?.[currentDeviceType]?.scale || section.scale || 1})`,
                                                    width: '100%',
                                                    height: '100%'
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {isFullWidth ? (
                                        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/60 to-transparent pointer-events-none z-10" />
                                    ) : (
                                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent pointer-events-none z-10" />
                                    )}
                                </div>

                                {isFullWidth ? (
                                    <div className="absolute inset-0 flex items-center justify-between px-8 md:px-16 z-20">
                                        <div className="max-w-lg">
                                            <h3 className="text-3xl font-bold text-white tracking-tight mb-2">{section.title}</h3>
                                            <p className="text-gray-300">{section.subtitle}</p>
                                        </div>
                                        <div className="hidden md:flex items-center justify-center w-20 h-20 bg-white/10 backdrop-blur rounded-full group-hover:bg-white/20 transition-colors p-4">
                                            <img src="/CCSV_LOGO.svg" alt="Logo" className="w-full h-full object-contain filter drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]" />
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        {/* Standard Card Content */}
                                        <div className="absolute top-8 left-8 right-8 z-20">
                                            <span className="text-white/60 font-bold uppercase tracking-widest text-[10px] mb-2 block">Colección Premium</span>
                                            <h3 className="text-3xl md:text-4xl font-bold text-white tracking-tight leading-tight">{section.title}</h3>
                                            <p className="text-gray-300 mt-2 text-sm md:text-base line-clamp-2">{section.subtitle}</p>
                                        </div>

                                        <div className="absolute bottom-8 left-8 z-20">
                                            <span className="inline-flex items-center gap-2 bg-white text-black px-6 py-3 rounded-full font-bold text-[10px] uppercase tracking-wide group-hover:bg-gray-200 transition-colors shadow-[0_0_20px_rgba(255,255,255,0.3)]">
                                                {section.cta_text || 'Ver Catálogo'}
                                            </span>
                                        </div>
                                    </>
                                )}

                                {/* Shine Effect */}
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 pointer-events-none" />
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default AppleStyleGrid;
